/* ───────────────────────────────────────────────────────────────────────────
   StudentApp — entry point for the TUAP student experience.

   Opened via a clean link (/firstname-lastname-xxxxx) or the legacy
   /student?id=&t= link. Before the wizard/portal loads, the visitor must
   verify their email with a 6-digit code — only the student, parents, shared
   emails and the NM Squad team can get in. Verification is remembered per
   device. Shows the onboarding wizard until complete, then the portal.
   ─────────────────────────────────────────────────────────────────────────── */

import { useState, useEffect } from "react";
import {
  getTuapStudent, getTuapStudentBySlug, subscribeTuapStudent,
  requestEditAccess, allowedEmails,
} from "./tuap";
import { sendOtp, verifyOtp } from "./mailer";
import OnboardingWizard from "./OnboardingWizard";
import StudentPortal from "./StudentPortal";

const GOLD = "#F2B100", GOLD_LO = "#8A6400";
const INK = "#1A1A1E", TXT = "#5C5C66", MUTE = "#9A9AA2", LINE = "#E6E6E9", BG = "#F6F6F7";

function Screen({ children }) {
  return (
    <div style={{ minHeight: "100vh", fontFamily: "'Archivo',-apple-system,sans-serif",
      background: BG, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800;900&family=Space+Mono:wght@700&display=swap');`}</style>
      {children}
    </div>
  );
}

const card = {
  maxWidth: 420, width: "100%", background: "#fff", border: `1.5px solid ${LINE}`,
  borderRadius: 14, padding: "34px 30px",
};
const inp = {
  width: "100%", fontFamily: "'Archivo',sans-serif", fontSize: 15, fontWeight: 600,
  color: INK, padding: "12px 14px", border: `1.5px solid ${LINE}`, borderRadius: 8,
  outline: "none", boxSizing: "border-box",
};
const btn = {
  width: "100%", fontFamily: "'Space Mono',monospace", fontSize: 12.5, fontWeight: 700,
  letterSpacing: ".05em", textTransform: "uppercase", cursor: "pointer", border: "none",
  borderRadius: 8, padding: "13px", background: GOLD, color: "#fff", marginTop: 14,
};

/* ── Email verification gate ── */
function Gate({ student, onDone }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function send() {
    const e = email.trim().toLowerCase();
    setErr("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setErr("Enter a valid email address."); return; }
    if (!allowedEmails(student).has(e)) {
      setErr("This email doesn't have access to this portal. Use the email you onboarded with, or ask NM Squad to share access.");
      return;
    }
    setBusy(true);
    const r = await sendOtp(student.id, e);
    setBusy(false);
    if (r.ok) { setStep("code"); }
    else { setErr("Couldn't send the code. Please try again in a moment."); }
  }
  async function check() {
    setErr("");
    setBusy(true);
    const r = await verifyOtp(student.id, email.trim().toLowerCase(), code.trim());
    setBusy(false);
    if (r.ok) onDone(email.trim().toLowerCase());
    else setErr("That code is wrong or expired. Check your email or resend.");
  }

  return (
    <Screen>
      <div style={card}>
        <div style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, fontWeight: 700,
          letterSpacing: ".14em", color: GOLD_LO, textTransform: "uppercase" }}>NM Squad</div>
        <h1 style={{ fontSize: 23, fontWeight: 900, color: INK, margin: "8px 0 6px",
          letterSpacing: "-.02em" }}>Verify it's you</h1>
        {step === "email" ? (
          <>
            <p style={{ fontSize: 13.5, color: TXT, lineHeight: 1.55, marginBottom: 18 }}>
              Enter your email and we'll send a 6-digit code. Only the student, parents and
              the NM Squad team can open this portal.
            </p>
            <input style={inp} type="email" placeholder="your@email.com" value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()} autoFocus />
            <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={send}>
              {busy ? "Sending…" : "Send code"}
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13.5, color: TXT, lineHeight: 1.55, marginBottom: 18 }}>
              We sent a 6-digit code to <b style={{ color: INK }}>{email}</b>. Enter it below.
            </p>
            <input style={{ ...inp, letterSpacing: ".3em", fontSize: 19, textAlign: "center" }}
              inputMode="numeric" maxLength={6} placeholder="······" value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={e => e.key === "Enter" && check()} autoFocus />
            <button style={{ ...btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={check}>
              {busy ? "Verifying…" : "Verify & enter"}
            </button>
            <button onClick={() => { setStep("email"); setCode(""); setErr(""); }}
              style={{ width: "100%", background: "none", border: "none", color: MUTE,
                fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginTop: 12 }}>
              Use a different email
            </button>
          </>
        )}
        {err && <div style={{ fontSize: 12.5, fontWeight: 600, color: "#c0392b",
          marginTop: 12, lineHeight: 1.5 }}>{err}</div>}
      </div>
    </Screen>
  );
}

export default function StudentApp({ forcedSlug, essayOnly }) {
  const [student, setStudent] = useState(null);
  const [state, setState] = useState("loading");  // loading | denied | ready
  const [verified, setVerified] = useState(false);
  const [view, setView] = useState(null);          // wizard | portal

  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");
  const token = params.get("t");
  const slug = forcedSlug || window.location.pathname.replace(/^\/+|\/+$/g, "").toLowerCase();

  useEffect(() => {
    let unsub;
    (async () => {
      let s = null;
      try {
        if (id && token) {
          s = await getTuapStudent(id);
          if (!s || s.accessToken !== token) s = null;
        } else if (slug && slug !== "student") {
          s = await getTuapStudentBySlug(slug);
        }
      } catch (e) { s = null; }
      if (!s) { setState("denied"); return; }
      // Remembered verification for this device?
      const saved = (localStorage.getItem("tuap_v_" + s.id) || "").toLowerCase();
      if (saved && allowedEmails(s).has(saved)) setVerified(true);
      setView(s.onbComplete ? "portal" : "wizard");
      setStudent(s);
      setState("ready");
      unsub = subscribeTuapStudent(s.id, fresh => { if (fresh) setStudent(fresh); });
    })();
    return () => unsub && unsub();
  }, [id, token, slug]);

  if (state === "loading") {
    return (
      <Screen>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 36, height: 36, borderRadius: 999, border: `3px solid ${LINE}`,
            borderTopColor: GOLD, margin: "0 auto", animation: "spin 1s linear infinite" }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ marginTop: 14, color: MUTE, fontSize: 14, fontWeight: 600 }}>Loading…</div>
        </div>
      </Screen>
    );
  }

  if (state === "denied") {
    return (
      <Screen>
        <div style={{ ...card, textAlign: "center" }}>
          <h1 style={{ fontSize: 22, fontWeight: 900, color: INK, letterSpacing: "-.02em" }}>
            This link isn't valid</h1>
          <p style={{ fontSize: 13.5, color: TXT, lineHeight: 1.6, margin: "12px 0 0" }}>
            Open your portal through the personal link your Student Success Officer shared.
            If you've lost it, just ask them to resend it.
          </p>
        </div>
      </Screen>
    );
  }

  if (!verified) {
    return (
      <Gate student={student} onDone={email => {
        localStorage.setItem("tuap_v_" + student.id, email);
        setVerified(true);
      }} />
    );
  }

  // Dedicated essay-submission link — just the Essays tab.
  if (essayOnly) {
    return <StudentPortal student={student} onlyTab="Essays" />;
  }

  if (view === "portal") {
    return (
      <StudentPortal
        student={student}
        onRequestEdit={() => {
          if (student.editRequestedAt) return;
          requestEditAccess(student).catch(() => {});
        }}
      />
    );
  }

  return <OnboardingWizard student={student} onComplete={() => setView("portal")} />;
}
